/**
 * CVFilterBar component for CVs page.
 * Provides Pool, Position, Job multi-select filters and a search box.
 */

import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, FilterCombobox, MultiSelectCombobox, SearchInput, useAuth, useDebounce } from '@aaas/shared';
import type { FilterOption } from '@aaas/shared';
import { usePoolListQuery } from '../hooks/usePoolQueries';
import { usePositionListQuery } from '../hooks/usePositionQueries';
import { useJobListQuery } from '../hooks/useJobQueries';
import { useTagListQuery } from '../hooks/useTagQueries';
import { PROCESSING_STATUS_CONFIG } from '../types';
import type { CVProcessingStatus } from '../types';

export interface CVFilters {
  search?: string;
  processingStatus?: string;
  poolIds?: string[];
  positionIds?: string[];
  jobIds?: string[];
  cvTagSlugs?: string[];
}

interface CVFilterBarProps {
  filters: CVFilters;
  onFiltersChange: (filters: CVFilters) => void;
}

export function CVFilterBar({ filters, onFiltersChange }: CVFilterBarProps) {
  const { currentWorkspace, idToken } = useAuth();
  const { t } = useTranslation('ats');
  const [tagSearch, setTagSearch] = useState('');
  const debouncedTagSearch = useDebounce(tagSearch, 300);

  // Fetch filter options
  const { data: poolResponse, isLoading: poolsLoading } = usePoolListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page: 1,
    size: 100,
    enabled: !!currentWorkspace && !!idToken,
    refetchInterval: false,
  });

  const { data: positionResponse, isLoading: positionsLoading } = usePositionListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page: 1,
    size: 100,
    enabled: !!currentWorkspace && !!idToken,
    refetchInterval: false,
  });

  const { data: jobResponse, isLoading: jobsLoading } = useJobListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page: 1,
    size: 100,
    enabled: !!currentWorkspace && !!idToken,
    refetchInterval: false,
  });

  const { data: tagResponse, isLoading: tagsLoading } = useTagListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    search: debouncedTagSearch || undefined,
    enabled: !!currentWorkspace && !!idToken,
  });

  // Status options from static config
  const statusOptions = useMemo<FilterOption[]>(
    () =>
      (Object.keys(PROCESSING_STATUS_CONFIG) as CVProcessingStatus[]).map((status) => ({
        value: status,
        label: PROCESSING_STATUS_CONFIG[status].label,
      })),
    []
  );

  // Transform data to FilterOption format
  const poolOptions = useMemo<FilterOption[]>(
    () =>
      (poolResponse?.data?.items || []).map((p) => ({
        value: p.id,
        label: p.name,
      })),
    [poolResponse]
  );

  const positionOptions = useMemo<FilterOption[]>(
    () =>
      (positionResponse?.data?.items || []).map((p) => ({
        value: p.id,
        label: p.name,
      })),
    [positionResponse]
  );

  const jobOptions = useMemo<FilterOption[]>(
    () =>
      (jobResponse?.data?.items || []).map((j) => ({
        value: j.id,
        label: `${j.code} - ${j.position.name}`,
      })),
    [jobResponse]
  );

  const tagOptions = useMemo<FilterOption[]>(
    () =>
      (tagResponse?.data || []).map((t) => ({
        value: t.slug,
        label: t.name,
      })),
    [tagResponse]
  );

  const hasActiveFilters =
    filters.search ||
    filters.processingStatus ||
    (filters.poolIds && filters.poolIds.length > 0) ||
    (filters.positionIds && filters.positionIds.length > 0) ||
    (filters.jobIds && filters.jobIds.length > 0) ||
    (filters.cvTagSlugs && filters.cvTagSlugs.length > 0);

  const handleClearFilters = () => {
    onFiltersChange({});
  };

  const updateArrayFilter = (key: 'poolIds' | 'positionIds' | 'jobIds' | 'cvTagSlugs', values: string[]) => {
    onFiltersChange({
      ...filters,
      [key]: values.length > 0 ? values : undefined,
    });
  };

  const updateFilter = (key: 'processingStatus', value: string | undefined) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const handleSearch = (value: string) => {
    onFiltersChange({
      ...filters,
      search: value || undefined,
    });
  };

  // Count active filters
  const activeFilterCount = [
    filters.search,
    filters.processingStatus,
    filters.poolIds && filters.poolIds.length > 0 ? true : undefined,
    filters.positionIds && filters.positionIds.length > 0 ? true : undefined,
    filters.jobIds && filters.jobIds.length > 0 ? true : undefined,
    filters.cvTagSlugs && filters.cvTagSlugs.length > 0 ? true : undefined,
  ].filter(Boolean).length;

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-gray-50 rounded-lg border">
      {/* Search */}
      <SearchInput
        placeholder={t('cv.searchPlaceholder')}
        onSearch={handleSearch}
        initialValue={filters.search || ''}
        width="lg"
      />

      {/* Status Filter */}
      <FilterCombobox
        options={statusOptions}
        value={filters.processingStatus}
        onChange={(value) => updateFilter('processingStatus', value)}
        placeholder={t('cvFilter.status')}
        searchPlaceholder={t('positionConfig.statusSearchPlaceholder')}
        emptyMessage={t('positionConfig.statusEmpty')}
        className="w-[140px]"
      />

      {/* Pool Filter */}
      <MultiSelectCombobox
        options={poolOptions}
        values={filters.poolIds || []}
        onChange={(values) => updateArrayFilter('poolIds', values)}
        placeholder={t('cvFilter.pool')}
        searchPlaceholder={t('pool.searchPlaceholder')}
        emptyMessage={t('pool.empty')}
        isLoading={poolsLoading}
        className="w-[180px]"
      />

      {/* Position Filter */}
      <MultiSelectCombobox
        options={positionOptions}
        values={filters.positionIds || []}
        onChange={(values) => updateArrayFilter('positionIds', values)}
        placeholder={t('cvFilter.position')}
        searchPlaceholder={t('positionConfig.searchPlaceholder')}
        emptyMessage={t('positionConfig.empty')}
        isLoading={positionsLoading}
        className="w-[180px]"
      />

      {/* Job Filter */}
      <MultiSelectCombobox
        options={jobOptions}
        values={filters.jobIds || []}
        onChange={(values) => updateArrayFilter('jobIds', values)}
        placeholder={t('cvFilter.job')}
        searchPlaceholder={t('job.searchPlaceholder')}
        emptyMessage={t('job.empty')}
        isLoading={jobsLoading}
        className="w-[200px]"
      />

      {/* Tag Filter */}
      <MultiSelectCombobox
        options={tagOptions}
        values={filters.cvTagSlugs || []}
        onChange={(values) => updateArrayFilter('cvTagSlugs', values)}
        onSearchChange={setTagSearch}
        placeholder={t('cvFilter.tag')}
        displayName="tags"
        searchPlaceholder={t('common.search')}
        emptyMessage={t('cv.empty')}
        isLoading={tagsLoading}
        className="w-[180px]"
      />

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearFilters}
          className="text-gray-500 hover:text-gray-700"
        >
          <X className="h-4 w-4 mr-1" />
          {t('common.clear')} ({activeFilterCount})
        </Button>
      )}
    </div>
  );
}
